import * as React from "react";
import api, { $ } from "kw/api";
import { jsonStringToCamelCase } from "kanopi-components/utils/json";
import { policyFields, quoteFields, riskProfileFields } from "kw/api/fragments";
import { useGlobal } from "kanopi-components/useGlobal";
import { useGlobalsContext } from "kw/util/Globals";
import { useNavigate } from "@reach/router";
import { useTrackReferralRequired } from "../../quoting/hooks/useTracking";

export function usePolicy(policyId: TPolicy["id"]): [
  policy: TPolicy,
  extra: {
    repriceQuote: () => void;
    policyLoading: boolean;
    priceLoading: boolean;
    pricingFailed: boolean;
    fetchPolicy: () => void;
  }
] {
  const globalsContext = useGlobalsContext();
  const [data = {}, setData] = useGlobal(globalsContext);
  const [policy, setPolicy] = React.useState(null);
  const navigate = useNavigate();
  const trackReferralRequired = useTrackReferralRequired({ referralType: "broker" });
  const { reprice = false, pricingFailed = false } = data;
  const [policyLoading, setPolicyLoading] = React.useState(false);
  const [priceLoading, setPriceLoading] = React.useState(false);
  const [, setProduct] = useGlobal("product");
  function repriceQuote() {
    setData({ reprice: true });
  }

  async function fetchPolicy() {
    setPolicyLoading(true);
    try {
      const response = await api.query(getPolicyQuery(policyId));
      const policyResponse = response?.policy || {};

      const policyResponseWithRiskDataObj = {
        ...policyResponse,

        // Convert riskProfile.riskData from a string to an object
        riskProfile: {
          ...policyResponse?.riskProfile,
          riskData: jsonStringToCamelCase(policyResponse?.riskProfile?.riskData),
        },

        // convert draftUpdate.riskProfile.riskData from a string to an object
        draftUpdate: {
          ...policyResponse?.draftUpdate,
          riskProfile: {
            ...policyResponse?.draftUpdate?.riskProfile,
            riskData: jsonStringToCamelCase(
              policyResponse?.draftUpdate?.riskProfile?.riskData
            ),
          },
        },
      };

      setPolicy(policyResponseWithRiskDataObj);
      setProduct({
        key: response?.policy?.quote?.product?.key,
      });
    } catch (e) {
      navigate("/");
    } finally {
      setPolicyLoading(false);
    }
  }

  React.useEffect(() => {
    if (shouldLoad(policyId)) {
      fetchPolicy();
    }
  }, [policyId]);

  React.useEffect(() => {
    async function priceRiskProfile() {
      setPriceLoading(true);
      setData({ pricingFailed: false });
      try {
        const response = await api.mutation(getPriceRiskProfileQuery(), {
          input: {
            riskProfileId: policy.draftUpdate.riskProfile.id,
          },
        });
        const riskProfileResponse = response?.priceRiskProfile || {};
        const quotes = riskProfileResponse?.quotes || [];

        setPolicy({
          ...policy,
          draftUpdate: {
            riskProfile: {
              ...riskProfileResponse,
              riskData: jsonStringToCamelCase(riskProfileResponse?.riskData),
            },
            quotes,
          },
        });
        // TODO: consider setting the new riskprofile in globals as well to show updated coversections sooner
      } catch (e) {
        trackReferralRequired({
          referralReason: "Error",
          errors: e?.response?.errors || [e],
        });
        // Occasionally BZ takes forever to get a quote, and the server will kill the dyno. When this happens, we want
        // to give the user the ability to retry. Flag this in the global config so we can display a retry button
        // appropriately.
        setData({ pricingFailed: true });
      } finally {
        setData({ reprice: false });
        setPriceLoading(false);
      }
    }

    if (reprice && policy?.draftUpdate?.riskProfile?.id) {
      priceRiskProfile();
    }
  }, [policy?.draftUpdate?.riskProfile?.id, reprice]);

  return [
    policy,
    { repriceQuote, policyLoading, priceLoading, pricingFailed, fetchPolicy },
  ];
}

function shouldLoad(policyId, reprice = false) {
  if (policyId || reprice) {
    return true;
  }
  return false;
}

export function getPolicyQuery(policyId) {
  return {
    policy: [
      {
        id: policyId,
      },
      {
        ...policyFields,
      },
    ],
  };
}

export function getPriceRiskProfileQuery() {
  return {
    priceRiskProfile: [
      {
        input: $("input", "RiskProfileInput!"),
      },
      {
        ...riskProfileFields,
        quotes: {
          ...quoteFields,
        },
      },
    ],
  };
}
